/**
 * SourceCard — TASK-690.
 *
 * `MorningDigest`, `ResearchBrief`, `news_search` and `regulatory_feed` all surfaced external
 * links as bare URLs. A bare URL is the difference between "an AI dumped links at me" and a
 * citation an RM will click.
 *
 * The honesty rule: when the preview could not be fetched, the card shows the PUBLISHER DOMAIN
 * and nothing else. It never falls back to a title invented from the URL slug — a fabricated
 * headline on a citation is worse than no headline.
 */

import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';

import { httpClient } from '../../lib/httpClient';

export interface LinkPreview {
  url: string;
  resolved_url: string | null;
  title: string | null;
  description: string | null;
  image: string | null;
  site_name: string | null;
  favicon: string | null;
  unfurled: boolean;
  reason: string | null;
}

/** Batch-fetch previews for a set of URLs. Cached server-side; deduped by the query key. */
export function useLinkPreviews(urls: string[]) {
  const unique = Array.from(new Set(urls.filter(Boolean))).slice(0, 12);
  return useQuery({
    queryKey: ['link-previews', unique],
    queryFn: () =>
      httpClient.post<{ previews: LinkPreview[] }>('/api/portfolio/link-previews', {
        urls: unique,
      }),
    enabled: unique.length > 0,
    staleTime: 1000 * 60 * 30,
  });
}

export interface SourceCardProps {
  url: string;
  preview?: LinkPreview;
  /** Fallback label when there is no preview yet (e.g. the article headline we already hold). */
  fallbackTitle?: string;
  className?: string;
}

export function SourceCard({ url, preview, fallbackTitle, className = '' }: SourceCardProps) {
  const host = (() => {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  })();

  const title = preview?.unfurled ? preview.title : null;
  const publisher = preview?.site_name || host;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="source-card"
      className={`flex items-start gap-2 rounded-md border border-border bg-muted/30 px-2.5 py-2 transition-colors hover:bg-muted/60 ${className}`}
    >
      {preview?.favicon ? (
        <img
          src={preview.favicon}
          alt=""
          data-testid="source-card-favicon"
          className="mt-0.5 h-4 w-4 flex-shrink-0 rounded-sm"
          // A publisher with no favicon must not leave a broken-image glyph in a citation.
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      ) : (
        <ExternalLink className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
      )}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-medium text-foreground">
          {title || fallbackTitle || host}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
          <span data-testid="source-card-publisher">{publisher}</span>
          {/* Say the preview is unavailable rather than inventing one. */}
          {preview && !preview.unfurled && (
            <span data-testid="source-card-unavailable" className="italic">
              preview unavailable
            </span>
          )}
        </span>
        {preview?.unfurled && preview.description && (
          <span
            data-testid="source-card-description"
            className="mt-1 line-clamp-2 block text-[11px] text-muted-foreground"
          >
            {preview.description}
          </span>
        )}
      </span>
    </a>
  );
}

export default SourceCard;
